<template>
  <v-container fluid grid-list-md>
    <v-row>
      <v-col cols="12" justify-center>
        <v-card>
          <v-toolbar flat>
            <v-btn icon @click="exit"><v-icon>clear</v-icon></v-btn>
            <v-toolbar-title>Exception Detail</v-toolbar-title>
          </v-toolbar>
          <v-card-text>
            <v-container fluid style="padding: 0px;">
              <v-row wrap>
                <v-col cols="12" md="4" class="subheading">
                  Time: {{ formatDate(item.time) }}
                </v-col>
                <v-col cols="12" md="8" class="subheading">
                  <a :href="'/#/items/' + item.thing" v-if="item.type=='zoneTimeException'">{{ getThingName(item.thing) }}</a>
                  <span v-if="item.type=='zoneTimeException'">
                    was in <b>{{ getZoneName(item.zone) }}</b> for longer than the allowed <b>{{ zoneDuration }} minute(s)</b>.
                  </span>
                  <span v-else-if="item.type=='inventoryThingsMissing'">
                    Inventory shortage(s) in zone <b>{{ item.zone.name }}</b>.
                  </span>
                  <span v-else-if="item.type=='inventoryThingsUnexpected'">
                    Inventory overage(s) in zone <b>{{ item.zone.name }}</b>.
                  </span>
                  <span v-else>
                    was expected in <b>Zone 2</b> but was seen in <b>{{ getZoneName(item.zone) }}</b> instead.
                  </span>
                </v-col>
                <v-col cols="12" v-if="exceptionType === 'zoneTimeException'">
                  <v-card class="elevation-1">
                    <v-card-text>
                      <v-row wrap>
                        <v-col cols="12" md="5">
                          <date-time-view
                          v-model="dateStart"
                          clearable
                          label="Start Date"
                          ></date-time-view>
                        </v-col>
                        <v-col cols="12" md="5">
                          <date-time-view
                          v-model="dateEnd"
                          clearable
                          label="End Date"
                          ></date-time-view>
                        </v-col>
                        <v-col cols="12" md="2">
                          <v-btn @click="resetDate()">Reset</v-btn>
                        </v-col>
                      </v-row>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12">
                  <v-data-table
                  v-bind:headers="headers"
                  :items="items"
                  class="elevation-1"
                  :loading="loading"
                  :options.sync="pagination"
                  :footer-props="{'items-per-page-options':[10, 25, 100]}"
                  :server-items-length="exceptionType === 'zoneTimeException' ? totalItems : items.length"
                  >
                    <template slot="item" slot-scope="props">
                      <tr v-if="exceptionType === 'zoneTimeException'">
                        <td class="text-xs-left">
                          {{ props.item.tag }}
                          <template v-if="props.item.alarmTamper">- <v-icon small color="red">alarm</v-icon></template>
                        </td>
                        <td class="text-xs-left">
                          {{ formatDate(props.item.time) }}
                        </td>
                        <td class="text-xs-left">
                          {{ props.item.direction }}
                        </td>
                        <td class="text-xs-left">
                          {{ getEndpointName(props.item.endpoint) }}
                        </td>
                        <td class="text-xs-left">
                          {{ getLocationTagName(props.item.locationTag) }}
                        </td>
                        <td class="text-xs-left">
                          {{ props.item.antenna }}
                        </td>
                        <td class="text-xs-left">
                          {{ getZoneName(props.item.zone) }}
                        </td>
                      </tr>
                      <tr v-else>
                        <td class="text-xs-left">
                          {{ props.item.common.name }}
                        </td>
                        <td class="text-xs-left">
                          {{ props.item.common.serial }}
                        </td>
                        <td class="text-xs-left">
                          {{ formatCategories(props.item.common.categories) }}
                        </td>
                        <td class="text-xs-left">
                          {{ getTags(props.item.common.tags) }}
                        </td>
                        <td class="text-xs-center">
                          <a :href="'/#/items/' + props.item._id"><v-btn icon><v-icon>mdi-exit-to-app</v-icon></v-btn></a>
                        </td>
                      </tr>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <error-snackbar v-model="errorText"></error-snackbar>
  </v-container>
</template>

<script>
import moment from 'moment'
import ApiRequests from '@/api/ApiRequests'
import {EXCEPTIONTYPES} from '@/shared/constants'
import {LookupMixin, TimeMixin, SettingsMixin} from '@/shared/mixins'
import DateTimeView from '@/components/DateTimeView'
import ErrorSnackbar from '@sdgsystems/vue-common/components/ErrorSnackbar'
import { mapGetters } from 'vuex'

export default {
  components: {
    DateTimeView,
    ErrorSnackbar
  },

  mixins: [LookupMixin, TimeMixin, SettingsMixin],

  data () {
    return {
      headers: [],
      dateStart: null,
      dateEnd: null,
      endpoints: [],
      errorText: '',
      item: {},
      items: [],
      categories: [],
      exceptionType: '',
      loading: false,
      locationTags: [],
      oldParams: '',
      pagination: {
        itemsPerPage: 25
      },
      things: [],
      totalItems: 0,
      zones: []
    }
  },

  created () {
    Promise.all([
      ApiRequests.exceptionGet(this.$route.params.id).then(r => {
        this.item = r.data
        this.exceptionType = this.item.type
        this.items = this.item.multipleThings
      }).catch(e => {
        this.errorText = 'Error getting item: ' + e.response.data
      })
    ]).then(r => {
      this.resetDate()
    })

    // Will need to see if we can get rid of this,
    // only grabbing the information we need.
    ApiRequests.getEndpoints().then(r => {
      this.endpoints = r.data
    }).catch(e => {
      this.errorText = e.response.data
    })

    ApiRequests.queryLocationTags().then(r => {
      this.locationTags = r.data
    }).catch(e => {
      this.errorText = e.response.data
    })

    ApiRequests.getThings().then(r => {
      this.things = r.data
    }).catch(e => {
      this.errorText = e.response.data
    })

    ApiRequests.getZones().then(r => {
      this.zones = r.data
    }).catch(e => {
      this.errorText = e.response.data
    })
  },

  computed: {
    ...mapGetters([
      'siteSettings',
      'getCategories'
    ]),

    zoneDuration: function () {
      var d = this.getZone(this.item.zone).duration
      if (d) {
        return moment.duration(d / 1000000).asMinutes()
      }
      return d
    }
  },

  watch: {
    exceptionType: function () {
      this.prepHeader()
    },

    pagination: function () {
      this.getItems()
    },

    dateStart: function () {
      this.getItems()
    },

    dateEnd: function () {
      this.getItems()
    }
  },

  methods: {
    exit: function () {
      this.$router.go(-1)
    },

    getItems: function () {
      if (this.exceptionType === EXCEPTIONTYPES.zoneTimeExceptions) {
        this.loading = true
        var params = {
          'thing': this.item.thing
        }

        params.limit = this.pagination.itemsPerPage
        params.page = (this.pagination.page - 1)

        if (this.dateStart) {
          params.after = this.dateStart
        }
        if (this.dateEnd) {
          params.before = this.dateEnd
        }

        if (JSON.stringify(params) === this.oldParams || this.item.thing === undefined) {
          return
        }
        this.oldParams = JSON.stringify(params)
        ApiRequests.eventsQuery(params).then(r => {
          this.items = r.data
          this.totalItems = parseInt(r.headers['x-total-count'])
          this.loading = false
        }).catch(e => {
          this.errorText = e.response.data
        })
      }
    },

    resetDate: function () {
      this.dateStart = moment(this.item.time).startOf('day').format()
      this.dateEnd = moment(this.item.time).add(1, 'days').startOf('day').format()
    },

    prepHeader: function () {
      if (this.exceptionType === EXCEPTIONTYPES.zoneTimeExceptions) {
        this.headers = [
          {
            text: 'Tag',
            align: 'left',
            value: 'tag',
            sortable: false
          },
          {
            text: 'Time',
            align: 'left',
            value: 'time',
            sortable: false
          },
          {
            text: 'Direction',
            align: 'left',
            value: 'direction',
            sortable: false
          },
          {
            text: 'Endpoint',
            align: 'left',
            value: 'endpoint',
            sortable: false
          },
          {
            text: 'Location',
            align: 'left',
            value: 'state.locationTag',
            sortable: false
          },
          {
            text: 'Antenna',
            align: 'left',
            value: 'antenna',
            sortable: false
          },
          {
            text: 'Zone',
            align: 'left',
            value: 'zone',
            sortable: false
          }
        ]
      } else {
        this.headers = [
          {
            text: this.getItemNameLabel(this.siteSettings.itemNameLabel),
            align: 'left',
            value: 'common.name',
            sortable: false
          },
          {
            text: this.getItemSerialLabel(this.siteSettings.itemSerialLabel),
            align: 'left',
            value: 'common.serial',
            sortable: false
          },
          {
            text: 'Categories',
            align: 'left',
            value: 'common.categories',
            sortable: false
          },
          {
            text: 'Tag(s)',
            align: 'left',
            value: 'common.tags',
            sortable: false
          },
          {
            text: 'Actions',
            align: 'left'
          }
        ]
      }
    },

    getTags: function (tags) {
      return tags.join(', ')
    },

    formatCategories: function (categories) {
      if (categories == null || categories.length === 0) {
        return ''
      }
      var arr = []
      for (var x in categories) {
        for (let y of this.getCategories) {
          if (y.id === categories[x]) {
            arr.push(y.name)
          }
        }
      }
      if (arr.length !== 0) {
        return arr.toString().replaceAll(',', ', ')
      } else {
        return ''
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/ExceptionDetail.vue